import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  analyticsCacheReadResponseSchema,
  analyticsSnapshotSchema,
  greenlightEvaluationSchema,
  runDetailSchema,
} from '@internal/plugin-make-review-common';
import { abortable } from './abortable';
import type {
  MakeReviewApi,
  AgentSessionSummary,
  AnalyticsSnapshot,
  GetAnalyticsOverviewParams,
  GetRunsParams,
  GreenlightEvaluation,
  ReviewSessionView,
  RunDetail,
  RunsResponse,
  SessionsResponse,
} from './types';

/** Upper bound for the optional shared-snapshot read in the browser. */
const CACHE_READ_TIMEOUT_MS = 5_000;

export class AgenticReviewClient implements MakeReviewApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl('proxy')}/agentic-review`;
  }

  private async analyticsUrl(
    params: GetAnalyticsOverviewParams,
    suffix = '',
  ): Promise<string> {
    const search = new URLSearchParams();
    search.set('from', params.from);
    search.set('to', params.to);
    search.set('bucket', params.bucket);
    if (params.timeZone) search.set('timeZone', params.timeZone);
    if (params.workdaysOnly !== undefined)
      search.set('workdaysOnly', String(params.workdaysOnly));
    if (params.repo) search.set('repo', params.repo);
    const base = await this.discoveryApi.getBaseUrl('make-review');
    return `${base}/analytics/overview${suffix}?${search.toString()}`;
  }

  async getAnalyticsOverview(
    params: GetAnalyticsOverviewParams,
  ): Promise<AnalyticsSnapshot> {
    const res = await this.fetchApi.fetch(await this.analyticsUrl(params));
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return analyticsSnapshotSchema.parse(await res.json());
  }

  async getCachedAnalyticsOverview(
    params: GetAnalyticsOverviewParams,
    options?: { signal?: AbortSignal },
  ): Promise<AnalyticsSnapshot | null> {
    // The cache is optional, so a slow read must end instead of holding the
    // no-data error path open. One signal bounds discovery, the fetch wrapper
    // (which may still be resolving credentials), headers and body; the
    // caller's signal can only shorten it.
    const timeout = AbortSignal.timeout(CACHE_READ_TIMEOUT_MS);
    const signal = options?.signal
      ? AbortSignal.any([timeout, options.signal])
      : timeout;
    const read = async (): Promise<AnalyticsSnapshot | null> => {
      signal.throwIfAborted();
      const url = await this.analyticsUrl(params, '/cache');
      // A lookup that settles after the abort must not start a request.
      signal.throwIfAborted();
      const res = await this.fetchApi.fetch(url, { signal });
      if (!res.ok) {
        throw await ResponseError.fromResponse(res);
      }
      return analyticsCacheReadResponseSchema.parse(await res.json()).snapshot;
    };
    return abortable(read(), signal);
  }

  async getRuns(params: GetRunsParams): Promise<RunsResponse> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined)
      search.set('offset', String(params.offset));
    if (params.status) search.set('status', params.status);
    if (params.repo) search.set('repo', params.repo);

    const query = search.toString();
    const url = `${await this.baseUrl()}/runs${query ? `?${query}` : ''}`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return res.json() as Promise<RunsResponse>;
  }

  async getRun(runId: string): Promise<RunDetail> {
    const url = `${await this.baseUrl()}/runs/${encodeURIComponent(runId)}`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return runDetailSchema.parse(await res.json());
  }

  async getGreenlightEvaluation(
    runId: string,
  ): Promise<GreenlightEvaluation | null> {
    const url = `${await this.baseUrl()}/runs/${encodeURIComponent(
      runId,
    )}/greenlight/evaluation`;
    const res = await this.fetchApi.fetch(url);
    // The service answers 404 when the run or its Greenlight attempt is absent.
    if (res.status === 404) return null;
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return greenlightEvaluationSchema.parse(await res.json());
  }

  async listSessions(runId: string): Promise<AgentSessionSummary[]> {
    const url = `${await this.baseUrl()}/runs/${encodeURIComponent(
      runId,
    )}/sessions`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    const body = (await res.json()) as SessionsResponse;
    return body.sessions;
  }

  async getSession(
    runId: string,
    sessionId: string,
  ): Promise<ReviewSessionView> {
    const url = `${await this.baseUrl()}/runs/${encodeURIComponent(
      runId,
    )}/sessions/${encodeURIComponent(sessionId)}`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return res.json() as Promise<ReviewSessionView>;
  }

  async getSessionPreviewUrl(
    runId: string,
    sessionId: string,
    theme: 'light' | 'dark',
    search: string,
  ): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('make-review');
    // Iframe navigation cannot carry fetchApi's bearer header. Establish the
    // framework's HttpOnly user cookie before mounting the preview instead.
    const response = await this.fetchApi.fetch(
      `${baseUrl}/.backstage/auth/v1/cookie`,
      { credentials: 'include' },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);

    const params = new URLSearchParams({ theme });
    const selection = new URLSearchParams(search);
    for (const name of ['leafId', 'targetId']) {
      const value = selection.get(name);
      if (value) params.set(name, value);
    }
    return `${baseUrl}/runs/${encodeURIComponent(
      runId,
    )}/sessions/${encodeURIComponent(sessionId)}/preview?${params.toString()}`;
  }

  async downloadSessionRaw(runId: string, sessionId: string): Promise<string> {
    // The proxy injects the bearer token server-side, so a plain <a href>
    // download would be unauthenticated — fetch the body here instead and let
    // the caller build a Blob/object-URL to trigger the browser download.
    const url = `${await this.baseUrl()}/runs/${encodeURIComponent(
      runId,
    )}/sessions/${encodeURIComponent(sessionId)}/download?format=pi`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return res.text();
  }
}
